import type { MouseEventHandler } from 'react';
import { useIntl } from 'react-intl';

import { handleKeyboardClick } from '@/utils/accessibility';

import { Flex } from '@/components/Flex';
import { Typography } from '@/components/Typography';
import { Visibility } from '@/components/Visibility';
import type { WithChildren } from '@/lib/types';
import { commonMessages } from '@/messages/common';

import * as S from './CheckoutSection.styles';

const CheckoutSection = ({ children }: WithChildren) => (
  <S.Container data-test-id='CheckoutSectionContainer'>{children}</S.Container>
);

export const Header = ({
  children,
  className,
}: WithChildren & { className?: string }) => (
  <Typography
    className={className}
    data-test-id='CheckoutSectionHeader'
    fontFamily='italianPlate'
    mb={1}
    scale='h5'
    textAlign={{ xs: 'center', md: 'left' }}
    textTransform='uppercase'
  >
    {children}
  </Typography>
);

export const Subheader = ({ children }: WithChildren) => (
  <S.Subheader
    data-test-id='CheckoutSectionSubheader'
    textAlign={{ xs: 'center', md: 'left' }}
  >
    {children}
  </S.Subheader>
);

export interface SummaryProps extends WithChildren {
  className?: string;
  hasError?: boolean;
}

export const Summary = ({ children, className, hasError }: SummaryProps) => (
  <S.Summary
    className={className}
    data-test-id='CheckoutSectionSummary'
    hasError={hasError}
  >
    {children}
  </S.Summary>
);

export interface SummaryLineProps extends WithChildren {
  className?: string;
  isBold?: boolean;
}

export const SummaryLine = ({
  children,
  className,
  isBold,
}: SummaryLineProps) => (
  <S.SummaryLine
    className={className}
    data-test-id='CheckoutSectionSummaryLine'
    isBold={isBold}
  >
    {children}
  </S.SummaryLine>
);

export const SummaryError = ({ children }: WithChildren) => (
  <S.SummaryError data-test-id='CheckoutSectionSummaryError'>
    {children}
  </S.SummaryError>
);

interface SummaryEditProps {
  onClick: MouseEventHandler;
}

export const SummaryEdit = ({ onClick }: SummaryEditProps) => {
  const { formatMessage } = useIntl();

  return (
    <S.SummaryEdit
      data-testid='CheckoutSectionSummaryEdit'
      onClick={onClick}
      onKeyDown={handleKeyboardClick}
      role='button'
      tabIndex={0}
    >
      <Visibility mdVisible>
        <Typography scale='p2'>
          {formatMessage(commonMessages.change)}
        </Typography>
      </Visibility>
      <Visibility mdHidden>
        <S.IconWrapper>
          <S.EditIcon />
        </S.IconWrapper>
      </Visibility>
    </S.SummaryEdit>
  );
};

interface ActionButtonsProps {
  disabledCancel?: boolean;
  disabledSave?: boolean;
  isProcessing?: boolean;
  onCancel: MouseEventHandler;
  onSave: MouseEventHandler;
}

export const ActionButtons = ({
  disabledCancel,
  disabledSave,
  isProcessing = false,
  onCancel,
  onSave,
}: ActionButtonsProps) => {
  const { formatMessage } = useIntl();

  return (
    <Flex gridGap={2} justifyContent='center' pt={3}>
      <S.ActonButton
        data-test-id='CheckoutSectionActionButtonCancel'
        disabled={disabledCancel}
        onClick={onCancel}
        variant='outlined'
      >
        {formatMessage(commonMessages.cancel)}
      </S.ActonButton>
      <S.ActonButton
        data-test-id='CheckoutSectionActionButtonSave'
        disabled={disabledSave}
        isProcessing={isProcessing}
        onClick={onSave}
      >
        {formatMessage(commonMessages.save)}
      </S.ActonButton>
    </Flex>
  );
};

CheckoutSection.Header = Header;
CheckoutSection.Subheader = Subheader;
CheckoutSection.Summary = Summary;
CheckoutSection.SummaryLine = SummaryLine;
CheckoutSection.SummaryEdit = SummaryEdit;
CheckoutSection.ActionButtons = ActionButtons;
CheckoutSection.SummaryError = SummaryError;

export { CheckoutSection };
